import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Divider
} from "@mui/material";

import api from "../../api/api";

export default function ProjectBilling() {

  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);

  const [filters, setFilters] = useState({
    client: "",
    start: "",
    end: ""
  });

  const TAX_RATE = 18;

  const MINUTES_PER_DAY = 480;

  const handleChange = (e) => {

    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });

  };

  /* ======================================
     LOAD CLIENT LIST
  ====================================== */

  const loadClients = async () => {

    try {

      const res = await api.get("/billing/clients");
      setClients(res.data);

    } catch (err) {

      console.error("Client load error", err);

    }

  };

  /* ======================================
     FETCH EFFORT DATA
  ====================================== */

  const fetchEfforts = async () => {

    try {

      const res = await api.get("/reports/project-effort", {
        params: filters
      });

      const data = await Promise.all(

        res.data.map(async (r) => {

          const hours = r.minutes / 60;

          const days = r.minutes / MINUTES_PER_DAY;

          let rate = 0;

          if (r.project_id) {

            try {

              const rateRes = await api.get(
                `/billing/project-billing/${r.project_id}`
              );

              if (
                rateRes.data &&
                rateRes.data.rows &&
                rateRes.data.rows.length > 0
              ) {

                rate = rateRes.data.rows[0].rate || 0;

              }

            } catch (err) {

              console.log("Rate lookup failed", err);

            }

          }

          const amount = hours * rate;

          const tax = (amount * TAX_RATE) / 100;

          return {
            ...r,
            hours,
            days,
            rate,
            amount,
            tax,
            total: amount + tax
          };

        })
      );

      setRows(data);

    } catch (err) {

      console.error("Effort fetch error", err);

    }

  };

  /* ======================================
     GENERATE INVOICE
  ====================================== */

  const generateInvoice = async () => {

    try {

      const projectId =
        rows.length > 0 && rows[0].project_id
          ? rows[0].project_id
          : null;

      const res = await api.post("/billing/generate-invoice", {

        client_id: filters.client,
        project_id: projectId

      });

      alert("Invoice generated successfully");

      console.log(res.data);

    } catch (err) {

      console.error("Invoice generation error", err);

    }

  };

  /* ======================================
     SAVE INVOICE AS DRAFT
  ====================================== */

  const saveDraft = async () => {

    try {

      const projectId =
        rows.length > 0 && rows[0].project_id
          ? rows[0].project_id
          : null;

      const res = await api.post("/billing/save-draft", {

        client_id: filters.client,
        project_id: projectId,
        amount: total,

        rows: groupedRows,
        subtotal: subtotal,
        tax: tax,
        total: total

      });

      alert("Draft saved successfully");

      if (res.data && res.data.invoice_id) {

        const invoiceId = res.data.invoice_id;

        window.open(
          `http://localhost:5000/api/billing/invoice-preview/${invoiceId}`,
          "_blank"
        );

      }

    } catch (err) {

      console.error("Draft save error", err);
	  const message =
	   err.response?.data?.error ||
	   "All work items for the selected period are already billed. Duplicate billing is not allowed.";
	  alert(message);

    }

  };

  /* ======================================
     LOAD CLIENTS ON PAGE LOAD
  ====================================== */

  useEffect(() => {

    loadClients();

  }, []);

  /* ======================================
     GROUP ROWS BY PROJECT + USER
  ====================================== */

  const groupedRows = Object.values(

    rows.reduce((acc, r) => {

      const key = `${r.project}-${r.user}`;

      if (!acc[key]) {

        acc[key] = {
          ...r,
          days: 0,
          amount: 0,
          tax: 0,
          total: 0
        };

      }

      acc[key].days += r.days;
      acc[key].amount += r.amount;
      acc[key].tax += r.tax;
      acc[key].total += r.total;

      return acc;

    }, {})

  );

  /* ======================================
     CALCULATE TOTALS
  ====================================== */

  const subtotal = groupedRows.reduce((a, b) => a + b.amount, 0);

  const tax = groupedRows.reduce((a, b) => a + b.tax, 0);

  const total = groupedRows.reduce((a, b) => a + b.total, 0);

  return (

    <Box p={4}>

      <Typography variant="h5" mb={3}>
        Project Billing
      </Typography>

      <Card sx={{ mb: 3 }}>

        <CardContent>

          <Grid container spacing={2}>

            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Client"
                name="client"
                fullWidth
                value={filters.client}
                onChange={handleChange}
              >

                <MenuItem value="">Select Client</MenuItem>

                {clients.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}

              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="From"
                name="start"
                fullWidth
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="To"
                name="end"
                fullWidth
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                sx={{ height: "56px" }}
                onClick={fetchEfforts}
              >
                Fetch Efforts
              </Button>
            </Grid>

            <Grid item xs={12} md={1.5}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                sx={{ height: "56px" }}
                onClick={saveDraft}
              >
                Save Draft
              </Button>
            </Grid>

            <Grid item xs={12} md={1.5}>
              <Button
                variant="contained"
                color="success"
                fullWidth
                sx={{ height: "56px" }}
                onClick={generateInvoice}
              >
                Generate Invoice
              </Button>
            </Grid>

          </Grid>

        </CardContent>

      </Card>

      <Card>

        <CardContent>

          <Table>

            <TableHead>

              <TableRow>

                <TableCell>Service / Project</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Units</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Tax</TableCell>
                <TableCell>Total</TableCell>

              </TableRow>

            </TableHead>

            <TableBody>

              {groupedRows.map((r, i) => (

                <TableRow key={i}>

                  <TableCell>{r.project}</TableCell>
                  <TableCell>{r.user}</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>{r.days.toFixed(2)}</TableCell>
                  <TableCell>{r.rate}</TableCell>
                  <TableCell>{r.amount.toFixed(2)}</TableCell>
                  <TableCell>{r.tax.toFixed(2)}</TableCell>
                  <TableCell>{r.total.toFixed(2)}</TableCell>

                </TableRow>

              ))}

            </TableBody>

          </Table>

          <Box
            sx={{
              width: 320,
              ml: "auto",
              mt: 3,
              p: 2,
              borderRadius: 2,
              background: "#f8f9fc",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
            }}
          >

            <Typography variant="body1">
              Subtotal: <b>{subtotal.toFixed(2)}</b>
            </Typography>

            <Typography variant="body1">
              Tax ({TAX_RATE}%): <b>{tax.toFixed(2)}</b>
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography variant="h6">
              Total: <b>{total.toFixed(2)}</b>
            </Typography>

          </Box>

        </CardContent>

      </Card>

    </Box>

  );

}